import { createElement as rc, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { View, styled, Text, TextInput, Button, Modal, Card } from 'lib_ui-primitives';
import useCardBody from '../CardList/useCardBody';
import ListDataSource from '../dataSources';
import JsonViewer from '../../dataView/JsonViewer';

const CloseButton = styled(Button)`
    align-self: flex-end;
`;

const Cards = styled(View)`
    flex: 1 1 0;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: auto;
    gap: 12px;
    padding: 12px 4px;
    > * {
        flex-shrink: 0;
    }
`;

/** Display a bounded set of temporary records using the existing card columns.
 * The provider owns fetching, live updates and cleanup; this view never syncs
 * records to the client database or performs feature-specific service actions.
 */
export function LiveDataListView({ hNode, view }) {
    const { id, searchable, detailsProperty = 'details' } = hNode;
    const { records = [], viewReady, status, error, pagination } = view;
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const RowDetail = useCardBody(hNode, { autoHeight: true });
    const matching = search
        ? records.filter(row => JSON.stringify(row).toLowerCase().includes(search.toLowerCase()))
        : records;
    // prettier-ignore
    return rc(Fragment, null,
        rc(Cards, { id, 'aria-label': hNode.title || 'Live data' },
            status && rc(Text, { role: 'status' }, status),
            error && rc(Text, { role: 'alert' }, error),
            !viewReady && rc(Text, { role: 'status' }, 'Loading...'),
            pagination && rc(View, { style: { flexDirection: 'row', alignItems: 'center', gap: 12 } },
                rc(Button, { value: 'Newer', variant: 'secondary', size: 'sm', disabled: pagination.loading || !pagination.previous, onClick: pagination.previous }),
                rc(Text, null, pagination.label),
                rc(Button, { value: 'Older', variant: 'secondary', size: 'sm', disabled: pagination.loading || !pagination.next, onClick: pagination.next })
            ),
            searchable && rc(TextInput, {
                id: `${id}-search`, name: 'live-data-list-search', sequence: hNode.sequence || 0,
                value: search, onChange: setSearch, placeholder: 'Filter records', ariaLabel: 'Filter records'
            }),
            viewReady && !matching.length && rc(Text, null, search ? 'No matching records.' : 'No records yet.'),
            ...matching.map(item => rc(RowDetail, { key: item._id, item },
                detailsProperty && item[detailsProperty] != null && rc(Button, {
                    value: 'View details', variant: 'secondary', size: 'sm',
                    onClick: () => setSelected(item[detailsProperty])
                })
            ))
        ),
        rc(Modal, { id: `${id}-details`, visible: selected !== null },
            rc(Card, null,
                rc(CloseButton, { icon: 'x', variant: 'secondary', size: 'lg', alt: 'Close details', onClick: () => setSelected(null) }),
                selected !== null && rc(JsonViewer, { id: `${id}-details`, title: 'Details', tree: selected })
            )
        )
    );
}

export default function LiveDataList({ hNode }) {
    return rc(ListDataSource, { hNode }, view => rc(LiveDataListView, { hNode, view }));
}

LiveDataList.propTypes = { hNode: PropTypes.object.isRequired };
LiveDataListView.propTypes = { hNode: PropTypes.object.isRequired, view: PropTypes.object.isRequired };
